import React, { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  Calendar,
  X,
  RotateCcw,
  Clock,
  Badge,
  History,
  AlertTriangle,
} from "lucide-react";

import Alert from "../Alert";
import Loader from "src/components/common/Loader";
import NoData from "../NoData";
import CoachProfile from "../avatar/CoachProfile";
import ConfirmationModal from "../modals/common/ConfirmationModal";
import InfinitePaginatedList from "src/components/common/Pagination/InfinitePaginatedList";

import { formatDateToLocaleString } from "src/helpers/common";
import { useAppDispatch } from "src/redux/hooks";
import {
  getAppointmentsRequest,
  requestCancellationRequest,
} from "src/redux/slices/appointmentsSlice";
import { AppointmentData } from "src/types/appointment.types";
import { HEALTH_COACH_ROLE_MAPPING } from "src/constants/common";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";

const getStatusIcon = (status: string) => {
  const statusText = status.toLowerCase();
  switch (statusText) {
    case "completed":
      return (
        <div className="text-xs px-2 py-1 bg-green-100 text-green-700 border-0">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </div>
      );
    case "scheduled":
      return (
        <div className="rounded-xl text-xs px-2 py-1 bg-blue-100 text-blue-700 border-0 flex items-center justify-center">
          <Calendar className="w-3 h-3 mr-1 flex" />
          <span>Scheduled</span>
        </div>
      );
    case "cancelled":
      return (
        <div className="rounded-xl text-xs px-2 py-1 bg-red-100 text-red-700 border-0 flex items-center justify-center">
          <X className="w-3 h-3 mr-1" />
          <span>Cancelled</span>
        </div>
      );
    case "rescheduled":
      return (
        <Badge className="text-xs px-2 py-1 bg-orange-100 text-orange-700 border-0">
          <RotateCcw className="w-3 h-3 mr-1" />
          Rescheduled
        </Badge>
      );
    case "missed":
      return (
        <div className="rounded-xl text-xs px-2 py-1 bg-red-100 text-red-700 border-0 flex items-center justify-center">
          <X className="w-3 h-3 mr-1" />
          <span>Missed</span>
        </div>
      );
    default:
      return (
        <div className="rounded-xl text-xs px-2 py-1 bg-gray-100 text-gray-700 border-0 flex items-center justify-center">
          <Clock className="w-3 h-3 mr-1" />
          <span>Unknown</span>
        </div>
      );
  }
};

const AppointmentList: React.FC = () => {
  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState<
    string | null
  >(null);
  const [appointmentToCancel, setAppointmentToCancel] =
    useState<AppointmentData | null>(null);
  const [showCancelAppointmentModal, setShowCancelAppointmentModal] =
    useState(false);
  const params = useParams();
  const supportProgramKey = params["support-program-key"];

  const fetchAppointments = useCallback(
    async (page: number) => {
      return new Promise((resolve, reject) => {
        dispatch(
          getAppointmentsRequest({
            resolve,
            reject,
            payload: { page_no: page.toString() },
          })
        );
      }).then((response: any) => {
        const newAppointments: AppointmentData[] =
          response?.data?.appointment_list || [];

        return {
          items: newAppointments,
          hasMore: newAppointments.length === 10,
        };
      });
    },
    [dispatch, cancellingAppointmentId]
  );

  const handleRequestCancellation = async (appointment: AppointmentData) => {
    setCancellingAppointmentId(appointment.appointment_id);

    try {
      await new Promise<void>((resolve, reject) => {
        dispatch(
          requestCancellationRequest({
            resolve,
            reject,
            payload: {
              appointment_id: appointment.appointment_id,
              type: appointment?.type,
            },
          })
        );
      });

      // Event: CONSULTATION_CONFRIM_CANCEL_SUCCESS
      analytics.trackEvent(EVENT_NAMES.CONSULTATION_CONFRIM_CANCEL_SUCCESS, {
        screen_name: SCREEN_NAMES.BOOK_CONSULTATION_SCREEN,
        support_program_key: supportProgramKey,
        ...appointment,
      });

      // Refresh appointments after successful cancellation
      await new Promise((resolve, reject) => {
        dispatch(
          getAppointmentsRequest({
            resolve,
            reject,
            payload: { page_no: "1" },
          })
        );
      });
    } catch (error) {
      // Event: CONSULTATION_CONFRIM_CANCEL_ERROR
      analytics.trackEvent(EVENT_NAMES.CONSULTATION_CONFRIM_CANCEL_ERROR, {
        screen_name: SCREEN_NAMES.BOOK_CONSULTATION_SCREEN,
        support_program_key: supportProgramKey,
        ...appointment,
        error: (error as any)?.message || "Something went wrong",
      });
      console.error("Error cancelling appointment:", error);
    } finally {
      setCancellingAppointmentId(null);
      setAppointmentToCancel(null);
      setShowCancelAppointmentModal(false);
    }
  };

  const renderAppointment = useCallback(
    (appointment: AppointmentData, index: number) => {
      const formattedDate = formatDateToLocaleString(
        appointment.appointment_date
      );

      const handleCancelAppointment = () => {
        // Event: CONSULTATION_CANCEL_CLICKED
        analytics.trackEvent(EVENT_NAMES.CONSULTATION_CANCEL_CLICKED, {
          screen_name: SCREEN_NAMES.BOOK_CONSULTATION_SCREEN,
          support_program_key: supportProgramKey,
          ...appointment,
        });

        setAppointmentToCancel(appointment);
        setShowCancelAppointmentModal(true);
      };
      return (
        <div
          key={appointment.appointment_id}
          className="shadow-sm border-0 bg-white rounded-xl overflow-hidden"
        >
          <div className="p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: "var(--icon-bg-primary)" }}
                >
                  <CoachProfile
                    coachKey={HEALTH_COACH_ROLE_MAPPING[appointment?.role]}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-gray-800">
                    {appointment?.role}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {formattedDate} • {appointment.appointment_time}
                  </p>
                </div>
              </div>
              {getStatusIcon(appointment.appointment_status)}
            </div>
          </div>
          {appointment?.appointment_status === "Scheduled" && (
            <>
              <div className="border-t border-gray-100"></div>
              <div className="px-3 py-2 flex justify-between">
                <button
                  className="text-sm font-medium flex items-center gap-2"
                  style={{ color: "var(--status-error)" }}
                  onClick={handleCancelAppointment}
                  disabled={
                    cancellingAppointmentId === appointment.appointment_id
                  }
                >
                  {cancellingAppointmentId === appointment.appointment_id ? (
                    <>
                      <Loader size="sm" />
                      Cancelling...
                    </>
                  ) : (
                    "Cancel"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      );
    },
    [cancellingAppointmentId]
  );

  //const containerHeight = `calc(100vh - 57px - 305px)`;
  const containerHeight = useMemo(() => `calc(100vh - 57px - 305px)`, []);

  return (
    <div className="flex flex-col h-full">
      <div className="z-10">
        <h2 className="text-lg font-semibold text-gray-800 sticky top-0 pb-4">
          History
        </h2>
      </div>

      <div
        id="appointment-scroll-container"
        ref={scrollContainerRef}
        className={`h-[${containerHeight}] overflow-y-auto space-y-3`}
      >
        <InfinitePaginatedList<AppointmentData>
          fetchData={fetchAppointments}
          renderItem={renderAppointment}
          containerHeight={containerHeight}
          scrollableTarget="appointment-scroll-container"
          loader={
            <div className="flex justify-center items-center py-4">
              <Loader size="lg" />
            </div>
          }
          emptyMessage={
            <NoData icon={<History />} title="No appointments found" />
          }
        />
      </div>
      {showCancelAppointmentModal && (
        <ConfirmationModal
          show={showCancelAppointmentModal}
          handleClose={() => setShowCancelAppointmentModal(false)}
          title="Cancel Appointment"
          description={
            <div className="flex flex-col gap-4">
              <p>Are you sure you want to cancel this appointment ?</p>
              <Alert
                title="Disclaimer"
                description="On cancellation of the appointment, any applicable refund will be credited to your wallet as care coins."
                variant="warning"
                icon={<AlertTriangle />}
              />
            </div>
          }
          onConfirm={() => {
            if (appointmentToCancel) {
              // Event: CONSULTATION_CONFRIM_CANCEL_CLICKED
              analytics.trackEvent(
                EVENT_NAMES.CONSULTATION_CONFRIM_CANCEL_CLICKED,
                {
                  screen_name: SCREEN_NAMES.BOOK_CONSULTATION_SCREEN,
                  support_program_key: supportProgramKey,
                  ...appointmentToCancel,
                }
              );
              setShowCancelAppointmentModal(false);
              handleRequestCancellation(appointmentToCancel);
            }
          }}
          isLoading={
            cancellingAppointmentId === appointmentToCancel?.appointment_id
          }
        />
      )}
    </div>
  );
};

export default AppointmentList;
